import type { SxProps, Theme } from "@mui/material";
import type { KeyboardActionId } from "~/keyboard/actionCatalog";

import { Box } from "@mui/material";

import Keycap from "~/components/keyboard/Keycap";
import useHintBadgeMode from "~/hooks/useHintBadgeMode";
import useKeybindingOverrides from "~/hooks/useKeybindingOverrides";
import hintBadgeBinding from "~/keyboard/hintBadgeBinding";

export type HintBadgePlacement = "bottom-left" | "bottom-right" | "top-left" | "top-right";

interface ShortcutHintBadgeProps {
	actionId?: KeyboardActionId;
	binding?: string | null;
	forceVisible?: boolean;
	placement?: HintBadgePlacement;
}

const placementSx: Record<HintBadgePlacement, SxProps<Theme>> = {
	"bottom-left": { bottom: -6, left: -6 },
	"bottom-right": { bottom: -6, right: -6 },
	"top-left": { left: -6, top: -6 },
	"top-right": { right: -6, top: -6 }
};

const ShortcutHintBadge = ({
	actionId,
	binding,
	forceVisible = false,
	placement = "top-right"
}: ShortcutHintBadgeProps) => {
	const { enabled } = useHintBadgeMode();
	const { overrides } = useKeybindingOverrides();

	if (!enabled && !forceVisible) return null;
	const resolvedBinding = binding ?? (actionId ? hintBadgeBinding(actionId, overrides) : null);
	if (!resolvedBinding) return null;

	return (
		<Box
			sx={{
				...placementSx[placement],
				backgroundColor: "background.paper",
				borderRadius: 0.75,
				boxShadow: 2,
				pointerEvents: "none",
				position: "absolute",
				zIndex: (theme) => theme.zIndex.tooltip
			}}
		>
			<Keycap binding={resolvedBinding} />
		</Box>
	);
};

export default ShortcutHintBadge;
